import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';

const services = [
  {
    icon: 'ri-play-circle-line',
    title: '在线播放入口',
    desc: '为“善良的小姨子”等剧集提供清晰的在线观看入口，随时续看，操作简单直观。',
  },
  {
    icon: 'ri-book-read-line',
    title: '详尽剧情介绍',
    desc: '整理每部剧集的剧情简介、人物关系与分集信息，帮助你快速了解故事走向。',
  },
  {
    icon: 'ri-calendar-check-line',
    title: '剧集更新追踪',
    desc: '标注剧集更新状态与最新集数，让你第一时间掌握更新动态，不错过任何一集。',
  },
  {
    icon: 'ri-compass-3-line',
    title: '智能内容推荐',
    desc: '基于题材与标签的相关性推荐相似剧集，帮你在同一类型里发现更多好剧。',
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 善良的小姨子在线播放';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', '了解“善良的小姨子在线播放”影视内容站的定位、内容服务范围与联系方式，欢迎留言反馈。');
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们已经收到。');
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === 'contact_alt') return;
      const str = String(value).trim();
      if (str) params.append(key, str);
    });

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/db945lprh5tg6sock3bg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们已经收到，会尽快回复你。');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch (err) {
      setFormStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <Layout>
      <div className="pt-16 md:pt-[68px]">
        <section className="w-full bg-primary-950 px-4 md:px-16 lg:px-24 py-12 md:py-20">
          <nav className="flex items-center gap-1.5 text-xs text-background-100/60 mb-6">
            <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-100">关于</span>
          </nav>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 text-accent-400 text-xs font-medium mb-3">
              <i className="ri-heart-3-line" />
              关于我们
            </span>
            <h1 className="font-heading font-extrabold text-background-50 text-3xl md:text-5xl leading-tight">
              善良的小姨子在线播放
            </h1>
            <p className="text-background-100/75 text-sm md:text-base leading-relaxed mt-5">
              我们是一个专注影视内容的线上站点，以“善良的小姨子”为核心，延伸至家庭情感、都市生活、年代温情等温暖题材。
              我们希望通过清晰的剧集导航、详尽的剧情介绍与舒适的观看体验，让每一位影视爱好者都能轻松找到并沉浸在想看的作品中。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-16 lg:px-24 py-12 md:py-20">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-1 h-5 rounded-full bg-accent-500" />
            <span className="text-xs font-medium text-foreground-500 tracking-wide">服务范围</span>
          </div>
          <h2 className="font-heading font-bold text-foreground-950 text-2xl md:text-3xl mb-8">我们能为你提供什么</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {services.map((s) => (
              <div key={s.title} className="rounded-lg border border-background-200/70 bg-background-50 p-6">
                <span className="w-12 h-12 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center text-2xl">
                  <i className={s.icon} />
                </span>
                <h3 className="font-heading font-semibold text-foreground-950 text-base mt-4">{s.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mt-2">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-16 lg:px-24 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6 lg:gap-10">
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
              <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">联系方式</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mt-3">
                如果你对内容、合作或使用体验有任何疑问，欢迎通过以下方式与我们取得联系。
              </p>
              <ul className="mt-6 space-y-4 text-sm text-foreground-700">
                <li className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center text-lg">
                    <i className="ri-mail-line" />
                  </span>
                  service@shanliang-tv.example
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center text-lg">
                    <i className="ri-time-line" />
                  </span>
                  每日 09:00 - 22:00 在线服务
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center text-lg">
                    <i className="ri-map-pin-line" />
                  </span>
                  线上服务 · 覆盖全国
                </li>
              </ul>
              <div className="rounded-md bg-accent-50 border border-accent-200 p-4 mt-6">
                <p className="text-xs text-accent-900 leading-relaxed">
                  <i className="ri-information-line mr-1" />
                  本站为影视内容演示站点，全部剧集信息为示例数据，播放功能为前端演示，真实播放接口待接入。
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">留言反馈</h2>
              <p className="text-sm text-foreground-600 mt-3 mb-6">告诉我们你的想法，或想看的内容，我们会认真阅读每一条留言。</p>

              {formStatus === 'success' && (
                <div className="rounded-md bg-secondary-50 border border-secondary-200 px-4 py-3 mb-5 flex items-center gap-2 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-fill" />
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="rounded-md bg-accent-50 border border-accent-300 px-4 py-3 mb-5 flex items-center gap-2 text-sm text-accent-900">
                  <i className="ri-error-warning-fill" />
                  {formError}
                </div>
              )}

              <form
                id="about-feedback-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      你的称呼
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入称呼"
                      className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="fb-type" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    反馈类型
                  </label>
                  <select
                    id="fb-type"
                    name="subject"
                    required
                    defaultValue=""
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="" disabled>请选择反馈类型</option>
                    <option value="内容建议">内容建议</option>
                    <option value="播放问题">播放问题</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请输入你的留言（最多 500 字）"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-none"
                  />
                </div>

                <input
                  className="contact-alt-field"
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  {formStatus === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交留言
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}